import { CobaltIconBulletedListOutline } from '@cae/cobalt-react/icons/outline/CobaltIconBulletedListOutline';
import { CobaltIconCalendarAddOutline } from '@cae/cobalt-react/icons/outline/CobaltIconCalendarAddOutline';
import { CobaltIconDocumentOutline } from '@cae/cobalt-react/icons/outline/CobaltIconDocumentOutline';
import { CobaltIconFolderOutline } from '@cae/cobalt-react/icons/outline/CobaltIconFolderOutline';
import { CobaltIconHomeOutline } from '@cae/cobalt-react/icons/outline/CobaltIconHomeOutline';
import { CobaltIconInstructorOutline } from '@cae/cobalt-react/icons/outline/CobaltIconInstructorOutline';
import { CobaltIconTasksOutline } from '@cae/cobalt-react/icons/outline/CobaltIconTasksOutline';
import { CobaltIconUserManagementOutline } from '@cae/cobalt-react/icons/outline/CobaltIconUserManagementOutline';
import { CobaltIconBulletedListSolid } from '@cae/cobalt-react/icons/solid/CobaltIconBulletedListSolid';
import { CobaltIconCalendarAddSolid } from '@cae/cobalt-react/icons/solid/CobaltIconCalendarAddSolid';
import { CobaltIconDocumentSolid } from '@cae/cobalt-react/icons/solid/CobaltIconDocumentSolid';
import { CobaltIconFolderSolid } from '@cae/cobalt-react/icons/solid/CobaltIconFolderSolid';
import { CobaltIconHomeSolid } from '@cae/cobalt-react/icons/solid/CobaltIconHomeSolid';
import { CobaltIconInstructorSolid } from '@cae/cobalt-react/icons/solid/CobaltIconInstructorSolid';
import { CobaltIconTasksSolid } from '@cae/cobalt-react/icons/solid/CobaltIconTasksSolid';
import { CobaltIconUserManagementSolid } from '@cae/cobalt-react/icons/solid/CobaltIconUserManagementSolid';
import { APP_ROUTES } from '@routes/routes.constants';

import type { AppSideNavigationItem } from '../AppSideNavigation.types';

/**
 * Manage module navigation items
 *
 * Permissions from roles_permissions.md:
 * - ModuleManage_View_Any: Access Manage module + home
 *
 * Note: Uses absolute paths with :customerId parameter
 * Pattern: /manage/:customerId/[route]
 *
 * Factory function: receives organizationName to bake into the
 * company documents label (avoids needing a dynamicLabel flag).
 */
export function getManageNav(
  organizationName: string,
  isReservationsCalendarEnabled = false
): AppSideNavigationItem[] {
  return [
    {
      label: 'Home',
      path: APP_ROUTES.MANAGE_HOME,
      icon: CobaltIconHomeOutline,
      activeIcon: CobaltIconHomeSolid,
      permission: 'ModuleManage_View_Any',
    },
    {
      label: 'Reservation requests',
      path: APP_ROUTES.MANAGE_RESERVATION_REQUESTS,
      icon: CobaltIconCalendarAddOutline,
      activeIcon: CobaltIconCalendarAddSolid,
      permission: 'Reservations_View_Org',
      featureFlag: 'bookingRequestPage',
    },
    {
      label: 'Reservations',
      path: APP_ROUTES.MANAGE_RESERVATIONS,
      icon: CobaltIconBulletedListOutline,
      activeIcon: CobaltIconBulletedListSolid,
      permission: 'Reservations_View_Org',
      featureFlag: 'reservationsPageV2',
      ...(isReservationsCalendarEnabled
        ? {
            children: [
              {
                label: 'List',
                path: APP_ROUTES.MANAGE_RESERVATIONS_LIST,
                permission: 'Reservations_View_Org',
              },
              {
                label: 'Calendar',
                path: APP_ROUTES.MANAGE_RESERVATIONS_CALENDAR,
                permission: 'Reservations_View_Org',
              },
            ],
          }
        : {}),
    },
    {
      label: 'Records',
      path: APP_ROUTES.MANAGE_RECORDS,
      icon: CobaltIconTasksOutline,
      activeIcon: CobaltIconTasksSolid,
      permission: 'Records_View_Org',
    },
    {
      label: 'Invoices and payments',
      path: APP_ROUTES.MANAGE_INVOICES_PAYMENTS,
      icon: CobaltIconDocumentOutline,
      activeIcon: CobaltIconDocumentSolid,
      permission: 'Invoices_View_Org',
      featureFlag: 'invoicesV2WebApp',
    },
    {
      label: 'Documents',
      icon: CobaltIconFolderOutline,
      activeIcon: CobaltIconFolderSolid,
      path: APP_ROUTES.MANAGE_DOCUMENTS,
      permission: [
        'CustomerTrainingDocument_View_Org',
        'CAETrainingDocuments_View_All',
        'SubmittedTrainingDocuments_View_Org',
      ],
      operator: 'some',
      children: [
        {
          label: `${organizationName} (FAA)`,
          path: APP_ROUTES.MANAGE_DOCUMENTS_COMPANY,
          permission: 'CustomerTrainingDocument_View_Org',
        },
        {
          label: organizationName,
          path: APP_ROUTES.MANAGE_DOCUMENTS_CUSTOMER_DOCUMENTS,
          permission: 'CustomerTrainingDocument_View_Org',
          featureFlag: 'manageViewCustomerNonFaaDocuments',
        },
        {
          label: 'CAE documents',
          path: APP_ROUTES.MANAGE_DOCUMENTS_CAE,
          permission: 'CAETrainingDocuments_View_All',
        },
        {
          label: 'Submitted documents',
          path: APP_ROUTES.MANAGE_DOCUMENTS_SUBMITTED,
          permission: 'SubmittedTrainingDocuments_View_Org',
        },
      ],
    },
    {
      label: 'Instructors',
      path: APP_ROUTES.MANAGE_INSTRUCTORS_MANAGEMENT,
      icon: CobaltIconInstructorOutline,
      activeIcon: CobaltIconInstructorSolid,
      permission: 'InstructorApproval_View_Org',
      featureFlag: 'instructorManagementV2WebApp',
      requiresPart135: true,
    },
    {
      label: 'Users',
      path: APP_ROUTES.MANAGE_USERS,
      icon: CobaltIconUserManagementOutline,
      activeIcon: CobaltIconUserManagementSolid,
      permission: 'Users_View_Org',
    },
  ];
}
